/**
 * Автоматическое распознавание комнат.
 *
 * Планировка — это планарный граф: узлы + стены. Замкнутые контуры (грани графа)
 * и есть комнаты. Обходим полурёбра, на каждом узле выбирая следующее ребро
 * с минимальным поворотом — так получаем все грани, включая внешнюю,
 * которую затем отбрасываем по ориентации обхода.
 */
import { roomIdOf, type WallGraph } from "@/shared/plan";
import {
	angleOf,
	centroid,
	insetPolygon,
	normalizeAngle,
	pointInPolygon,
	signedArea,
	sub,
	type Pt,
} from "@/shared/lib/geometry";

export interface Room {
	id: string;
	/** Узлы контура по осевым линиям стен */
	nodeIds: string[];
	/** Стены контура: wallIds[i] соединяет nodeIds[i] и nodeIds[i+1] */
	wallIds: string[];
	/** Контур по осевым линиям */
	outline: Pt[];
	/** Контур по внутренним граням стен — по нему считается чистая площадь */
	inner: Pt[];
	/** Чистая площадь, мм² */
	area: number;
	/** Периметр по внутреннему контуру, мм */
	perimeter: number;
	/** Точка для подписи */
	label: Pt;
}

const edgeKey = (a: string, b: string) => `${a}->${b}`;

export const detectRooms = (plan: WallGraph): Room[] => {
	const walls = Object.values(plan.walls);
	if (walls.length < 3) return [];

	// Смежность: узел -> соседние узлы (с указанием стены)
	const adj = new Map<string, { node: string; wallId: string; angle: number }[]>();
	const push = (from: string, to: string, wallId: string) => {
		const a = plan.nodes[from];
		const b = plan.nodes[to];
		if (!a || !b) return;
		const list = adj.get(from) ?? [];
		list.push({ node: to, wallId, angle: angleOf(sub(b, a)) });
		adj.set(from, list);
	};
	for (const w of walls) {
		push(w.a, w.b, w.id);
		push(w.b, w.a, w.id);
	}
	for (const [key, list] of adj.entries()) {
		adj.set(
			key,
			list.toSorted((p, q) => p.angle - q.angle),
		);
	}

	/** Следующее полуребро при обходе грани: самый «крутой» поворот направо. */
	function nextHalfEdge(from: string, to: string) {
		const list = adj.get(to);
		if (!list || list.length === 0) return null;
		const back = angleOf(sub(plan.nodes[from], plan.nodes[to]));
		let best = list[0];
		let bestDelta = Infinity;
		for (const cand of list) {
			const delta = normalizeAngle(cand.angle - back);
			// Пропускаем нулевой поворот (то же самое ребро назад), кроме тупиков.
			const d = delta < 1e-9 ? Math.PI * 2 : delta;
			if (d < bestDelta) {
				bestDelta = d;
				best = cand;
			}
		}
		return best;
	}

	const visited = new Set<string>();
	const faces: { nodeIds: string[]; wallIds: string[]; poly: Pt[]; area: number }[] = [];

	for (const w of walls) {
		for (const [from, to] of [
			[w.a, w.b],
			[w.b, w.a],
		] as const) {
			if (visited.has(edgeKey(from, to))) continue;
			const nodeIds: string[] = [];
			const wallIds: string[] = [];
			let curFrom = from;
			let curTo = to;
			let curWallId = w.id;
			let guard = 0;
			let ok = true;
			while (guard++ < 4096) {
				const key = edgeKey(curFrom, curTo);
				if (visited.has(key)) {
					ok = curFrom === from && curTo === to;
					break;
				}
				visited.add(key);
				// nodeIds[i] -> nodeIds[i+1] проходит по стене wallIds[i]
				nodeIds.push(curFrom);
				wallIds.push(curWallId);
				const next = nextHalfEdge(curFrom, curTo);
				if (!next) {
					ok = false;
					break;
				}
				curFrom = curTo;
				curTo = next.node;
				curWallId = next.wallId;
			}
			if (!ok || nodeIds.length < 3) continue;
			const poly = nodeIds.map((id) => plan.nodes[id]).filter(Boolean) as Pt[];
			if (poly.length < 3) continue;
			faces.push({ nodeIds, wallIds, poly, area: signedArea(poly) });
		}
	}

	if (faces.length === 0) return [];

	// Внешняя грань — всегда самая большая по площади, независимо от направления
	// обхода. По знаку ориентации фильтровать нельзя: в некоторых графах комнаты и
	// наружный контур могут иметь одинаковый порядок обхода, и тогда комната исчезает.
	// Своя внешняя грань — у каждой отдельной группы стен: две квартиры на одном листе,
	// комната в стороне. Иначе наружный контур второй группы считался бы ещё одной
	// комнатой с тем же id, что и настоящая, — и на чертеже висела бы лишняя подпись.
	const component = new Map<string, number>();
	let components = 0;
	for (const start of Object.keys(plan.nodes)) {
		if (component.has(start)) continue;
		const stack = [start];
		component.set(start, components);
		while (stack.length > 0) {
			const node = stack.pop() as string;
			for (const next of adj.get(node) ?? []) {
				if (component.has(next.node)) continue;
				component.set(next.node, components);
				stack.push(next.node);
			}
		}
		components++;
	}
	const outerOf = new Map<number, (typeof faces)[number]>();
	for (const f of faces) {
		const group = component.get(f.nodeIds[0]) ?? -1;
		const current = outerOf.get(group);
		if (!current || Math.abs(f.area) > Math.abs(current.area)) outerOf.set(group, f);
	}
	const outers = new Set(outerOf.values());

	const rooms: Room[] = [];
	for (const f of faces) {
		if (outers.has(f)) continue;
		if (Math.abs(f.area) < 1e4) continue; // мусорные грани < 0.01 м²

		// Сдвигаем каждое ребро внутрь на половину толщины его стены.
		const offsets = f.nodeIds.map((_, i) => {
			const wall = plan.walls[f.wallIds[i]];
			return wall ? wall.thickness / 2 : 0;
		});
		const inner = insetPolygon(f.poly, offsets);
		const area = Math.abs(signedArea(inner));
		let perimeter = 0;
		for (let i = 0; i < inner.length; i++) {
			const p = inner[i];
			const q = inner[(i + 1) % inner.length];
			perimeter += Math.hypot(q.x - p.x, q.y - p.y);
		}
		rooms.push({
			id: roomIdOf(f.nodeIds),
			nodeIds: f.nodeIds,
			wallIds: f.wallIds,
			outline: f.poly,
			inner,
			area,
			perimeter,
			label: centroid(inner),
		});
	}
	return rooms;
};

/**
 * Узлы, которые едут, когда комнату тащат целиком; null — комната не стоит отдельно.
 *
 * Отдельно стоит комната, вся связная группа стен которой — её контур и перегородки
 * внутри неё. Комнату, у которой есть общая стена с соседней или стена, уходящая наружу,
 * не тащат: сдвиг увёл бы за собой и соседей, перекосив их.
 */
export const freestandingRoomNodes = (plan: WallGraph, room: Room): string[] | null => {
	const contour = new Set(room.nodeIds);
	const seen = new Set<string>(room.nodeIds);
	const queue = [...room.nodeIds];
	while (queue.length > 0) {
		const id = queue.pop() as string;
		for (const wall of Object.values(plan.walls)) {
			if (wall.a !== id && wall.b !== id) continue;
			const other = wall.a === id ? wall.b : wall.a;
			if (seen.has(other)) continue;
			const node = plan.nodes[other];
			// Узел снаружи комнаты — её стены связаны с чужими
			if (!node || (!contour.has(other) && !pointInPolygon(node, room.inner))) return null;
			seen.add(other);
			queue.push(other);
		}
	}
	return [...seen];
};
